import { ClodoError } from './errors.js';
import { joinPath, parentOf, nameOf } from './paths.js';
import { signedDownloadUrl, jellyfinImageUrl } from './urls.js';

// Statuts que api.php renvoie en cas de succès.
const OK_STATUSES = ['ok', 'user', 'login'];

// Champs des actions d'administration : le backend lit $_REQUEST sur chacun,
// donc une valeur absente doit partir en chaîne vide plutôt que d'être omise
// (sur updateUser, un mot de passe vide signifie « ne pas changer »).
function blankIfMissing(value){
    return (value === undefined || value === null) ? '' : value;
}

export class ClodoApi {
    // options :
    //   apiUrl    — l'unique endpoint PHP.
    //   storage   — { get, set, remove }, éventuellement asynchrone
    //               (localStorage côté web, AsyncStorage côté mobile).
    //   fetchImpl — injectable pour les tests ; fetch global par défaut.
    constructor(options){
        this.apiUrl = options.apiUrl;
        this.storage = options.storage;
        this.fetchImpl = options.fetchImpl || ((url, init) => fetch(url, init));
        this.sessionId = null;
        this.userLogin = null;
        // Appelé quand le serveur déclare la session morte : à brancher sur le
        // passage à l'écran de login.
        this.onExpired = null;
    }

    // Relit les identifiants persistés. Doit être appelé avant la première
    // requête : côté mobile le stockage est asynchrone, donc ceci ne peut pas
    // tenir dans le constructeur.
    async restore(){
        this.sessionId = (await this.storage.get('session_id')) || null;
        this.userLogin = (await this.storage.get('user_login')) || null;
        return this;
    }

    async _persistSession(sessionId, userLogin){
        if(sessionId){
            this.sessionId = sessionId;
            await this.storage.set('session_id', sessionId);
        }
        if(userLogin){
            this.userLogin = userLogin;
            await this.storage.set('user_login', userLogin);
        }
    }

    async _clearSession(){
        this.sessionId = null;
        this.userLogin = null;
        await this.storage.remove('session_id');
        await this.storage.remove('user_login');
    }

    // Ajoute le session_id au CORPS de la requête, jamais à l'URL — qui
    // finirait dans les journaux nginx.
    _appendSession(form){
        if(this.sessionId !== null){
            form.append('session_id', this.sessionId);
        }
        return form;
    }

    // Toutes les actions passent par ici : un POST multipart vers l'endpoint
    // unique. `files` permet de joindre des parties binaires, qui ont besoin du
    // troisième argument de FormData.append (le nom de fichier).
    async request(action, fields, files){
        const form = new FormData();
        form.append('action', action);
        const values = fields || {};
        Object.keys(values).forEach(key => {
            const value = values[key];
            if(value !== undefined && value !== null){
                form.append(key, value);
            }
        });
        (files || []).forEach(file => form.append(file.field, file.value, file.filename));
        this._appendSession(form);
        return this.send(form);
    }

    // Envoie un formulaire déjà constitué et normalise la réponse. Séparé de
    // request() pour que le chemin d'envoi de fichiers (qui a besoin de
    // construire son formulaire à part, cf. buildUploadForm) partage le même
    // traitement d'erreurs.
    async send(form){
        let response;
        try {
            response = await this.fetchImpl(this.apiUrl, { method : 'POST', body : form });
        } catch (cause) {
            throw new ClodoError('network', 'connexion impossible', cause);
        }
        if(!response.ok){
            throw new ClodoError('http', 'HTTP ' + response.status);
        }
        // api.php ne pose pas d'en-tête Content-Type : on parse le texte
        // nous-mêmes plutôt que de compter sur response.json().
        const text = await response.text();
        return this.parse(text);
    }

    // Exposé pour les transports qui ne passent pas par fetch (XHR côté web,
    // pour les événements de progression).
    parse(text){
        let data;
        try {
            data = JSON.parse(text);
        } catch (cause) {
            throw new ClodoError('parse', 'réponse illisible', text);
        }
        if(OK_STATUSES.indexOf(data.status) !== -1){
            return data;
        }
        if(data.status === 'expired'){
            this._clearSession();
            if(this.onExpired){ this.onExpired(); }
            throw new ClodoError('expired', 'session expirée', data);
        }
        // Les refus métier remontent leur motif dans `content`.
        throw new ClodoError('nok', String(data.content || 'action refusée'), data);
    }

    // --- authentification ---

    async login(username, password){
        // `path` est attendu par api.php mais ignoré côté serveur.
        const data = await this.request('login', { username : username, password : password, path : '' });
        await this._persistSession(data.session_id, data.user && data.user.name);
        return data;
    }

    async logout(){
        try {
            // L'action logout répond `expired` par construction : ici ce n'est
            // pas une erreur.
            await this.request('logout');
        } catch (error) {
            if(error.kind !== 'expired'){ throw error; }
        } finally {
            await this._clearSession();
        }
    }

    async getUser(){
        const data = await this.request('getUser');
        await this._persistSession(data.session_id, data.user && data.user.name);
        return data.user;
    }

    // --- appareils (jeton longue durée) ---

    // Échange un jeton d'appareil contre une session fraîche. Traité côté
    // serveur avant le portail d'authentification, donc utilisable sans session
    // valide — c'est ce qui permet à la sauvegarde photo de se réveiller après
    // plusieurs jours sans redemander le mot de passe (les sessions PHP expirent
    // en 2 h d'inactivité / 8 h absolues).
    async deviceLogin(deviceToken){
        const data = await this.request('deviceLogin', { device_token : deviceToken });
        await this._persistSession(data.session_id, data.user && data.user.name);
        return data;
    }

    // À appeler depuis une session déjà authentifiée : c'est l'unique moment où
    // le jeton transite. Le stocker ensuite dans le Keystore, pas dans un
    // stockage en clair.
    async registerDevice(deviceName){
        const data = await this.request('registerDevice', { device_name : deviceName });
        return data.device_token;
    }

    async listDevices(){
        const data = await this.request('listDevices');
        return data.devices;
    }

    // Accepte le jeton complet ou un préfixe d'au moins 12 caractères.
    revokeDevice(tokenOrPrefix){
        return this.request('revokeDevice', { device_token : tokenOrPrefix });
    }

    // --- journal de sauvegarde photo ---

    // Parmi `assetKeys`, renvoie celles déjà sauvegardées. Le serveur borne à
    // 500 clés par appel : découper les lots en amont.
    async syncCheck(assetKeys){
        const data = await this.request('syncCheck', { asset_keys : JSON.stringify(assetKeys) });
        return data.known || [];
    }

    // À appeler APRÈS un envoi réussi. Si le commit se perd, le cliché repart au
    // passage suivant et `noOverwrite` répond "file_exist" : il suffit alors de
    // committer. La panne ne produit jamais de doublon.
    syncCommit(entry){
        return this.request('syncCommit', {
            asset_key   : entry.assetKey,
            remote_path : entry.remotePath,
            size        : entry.size,
            device_id   : entry.deviceId
        });
    }

    // --- navigation et contenu ---

    get(path){
        return this.request('get', { path : joinPath(path) });
    }

    create(path){
        return this.request('create', { path : joinPath(path) });
    }

    rename(path, newName){
        return this.request('rename', { path : joinPath(path), newName : newName });
    }

    // `action` vaut 'copy' ou 'cut' : même contrat côté serveur.
    paste(path, newPath, action){
        return this.request(action, { path : joinPath(path), newPath : joinPath(newPath) });
    }

    remove(path){
        return this.request('delete', { path : joinPath(path) });
    }

    async search(query, limit){
        const data = await this.request('search', { query : query, limit : limit });
        return data.content;
    }

    // --- médias ---

    // Demande un lien signé frais (valable MEDIA_URL_TTL, 2 h) pour un fichier.
    async signUrl(path){
        const data = await this.request('signUrl', { path : joinPath(path) });
        return signedDownloadUrl(this.apiUrl, data.u, parentOf(path), nameOf(path), data.exp, data.sig);
    }

    // Lien signé construit à partir des champs dl_exp/dl_sig que l'action `get`
    // attache aux fichiers. Attention : seule `get` les fournit — les réponses
    // de mutation (upload, delete, rename, copy, cut, create) renvoient un
    // contenu où ils valent null, d'où la règle « re-get après mutation ».
    downloadUrl(pathArray, name, exp, sig){
        return signedDownloadUrl(this.apiUrl, this.userLogin, pathArray, name, exp, sig);
    }

    jellyfinImageUrl(id, exp, sig){
        return jellyfinImageUrl(this.apiUrl, id, exp, sig);
    }

    // --- envoi de fichiers ---

    // Formulaire d'un envoi, source unique de vérité des champs attendus par
    // Element::uploadFile. Partagé par le chemin fetch (mobile, arrière-plan) et
    // le chemin XHR (web, qui a besoin des événements de progression).
    //
    // `part` est ce que la plateforme sait mettre dans un FormData : un Blob
    // côté web, un { uri, name, type } côté React Native.
    //
    // Le nom de champ `file[]` est impératif : api.php itère
    // $_FILES['file']['name'] comme un tableau, un champ scalaire y provoque une
    // erreur fatale.
    buildUploadForm(part, options){
        const form = new FormData();
        form.append('action', 'upload');
        form.append('file[]', part, options.filename);
        form.append('path', joinPath(options.path || []));
        form.append('currentPath', joinPath(options.currentPath));
        form.append('chunkIndex', options.chunkIndex || 0);
        form.append('chunkOffset', options.chunkOffset || 0);
        form.append('totalChunks', options.totalChunks || 0);
        if(options.noOverwrite){ form.append('noOverwrite', '1'); }
        if(options.minimalAck){ form.append('minimalAck', '1'); }
        return this._appendSession(form);
    }

    upload(part, options){
        return this.send(this.buildUploadForm(part, options));
    }

    // --- partages ---

    async createShare(path, password, expiresAt){
        const data = await this.request('createShare', {
            path : joinPath(path),
            password : password,
            expires_at : expiresAt
        });
        return data.token;
    }

    updateShare(token, expiresAt){
        return this.request('updateShare', { token : token, expires_at : expiresAt });
    }

    deleteShare(token){
        return this.request('deleteShare', { token : token });
    }

    // --- administration (réservé aux comptes is_admin) ---

    async listUsers(){
        const data = await this.request('listUsers');
        return data.users;
    }

    createUser(user){
        return this.request('createUser', this._userFields(user));
    }

    updateUser(user){
        return this.request('updateUser', this._userFields(user));
    }

    deleteUser(login){
        return this.request('deleteUser', { login : login });
    }

    _userFields(user){
        const fields = {};
        Object.keys(user).forEach(key => { fields[key] = blankIfMissing(user[key]); });
        return fields;
    }
}
